import { useCallback, useEffect, useState } from 'react';
import { useDashboard } from '../../context/dashboardContext';
import { LoadingModal } from '../../components/shareable/loadingModal';
import CustomerPagination from '../../components/shareable/customerPagination';
import { useSearch } from '../../context/SearchContext'; // Import search context

const ProductsPage = () => {
  const { getMultipleData } = useDashboard();
  const { searchQuery, updateSearch } = useSearch(); // Get search context
  const [allProducts, setAllProducts] = useState([]); // Store all products
  const [filteredProducts, setFilteredProducts] = useState([]); // Store filtered products
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Set the page in search context when component mounts
  useEffect(() => {
    updateSearch('', 'product');
    
    // Clear search when component unmounts
    return () => updateSearch('', '');
  }, [updateSearch]);

  // Filter products based on search query
  const filterProducts = useCallback((products, query) => {
    if (!query.trim()) return products;
    
    const searchTerm = query.toLowerCase().trim();
    return products.filter(product => {
      // Safely check each field with optional chaining and nullish coalescing
      return (
        (product["MODULE TYPE"]?.toLowerCase() || '').includes(searchTerm) ||
        (product["PRODUCT DETAILS"]?.toLowerCase() || '').includes(searchTerm) ||
        (product["PRODUCT CODE"]?.toLowerCase() || '').includes(searchTerm) ||
        (product["MODEL"]?.toLowerCase() || '').includes(searchTerm) ||
        (product["BRAND"]?.toLowerCase() || '').includes(searchTerm)
      );
    });
  }, []);

  const fetchData = useCallback(async () => {
    const products = await getMultipleData(["products"]);
    if (products.success) {
      setAllProducts(products.data);
      
      // Apply search filter if there's a query
      const filtered = filterProducts(products.data, searchQuery);
      setFilteredProducts(filtered);
    }
    setLoading(false);
  }, [getMultipleData, searchQuery, filterProducts]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply search filter when search query changes
  useEffect(() => {
    if (allProducts.length > 0) {
      const filtered = filterProducts(allProducts, searchQuery);
      setFilteredProducts(filtered);
      setCurrentPage(1); // Reset to first page when search changes
    }
  }, [searchQuery, allProducts, filterProducts]);

  // Pagination logic - use filteredProducts instead of all products
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return loading ? (
    <LoadingModal loading={loading} />
  ) : (
    <div className="p-6">
      <div className="relative mb-3 mt-3">
        <h2 className="text-2xl font-bold text-red-700 uppercase tracking-wide">
          All Products {searchQuery && `(Filtered: ${filteredProducts.length} found)`}
        </h2>
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
        {currentProducts?.length > 0 ? (
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Index
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Module Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Product Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentProducts.map((p, i) => (
                <tr key={p._id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {indexOfFirstItem + i + 1}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {p["MODULE TYPE"] || <span className="italic text-gray-400">N/A</span>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 whitespace-pre-line">
                    {p["PRODUCT DETAILS"] || <span className="italic text-gray-400">N/A</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="px-6 py-8 text-center text-sm text-gray-500">
            <div className="flex flex-col items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-400 mb-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              {searchQuery ? 'No products match your search' : 'No products available'}
            </div>
          </div>
        )}
      </div>

      {filteredProducts.length > 0 && (
        <div className="mt-4">
          <CustomerPagination
            totalPages={totalPages}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            totalItems={filteredProducts.length}
            itemsPerPage={itemsPerPage}
          />
        </div>
      )}
    </div>
  );
};

export default ProductsPage;